import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ClipboardList, FileText, Pencil, Trash2, ArrowRight } from "lucide-react";
import { useState } from "react";
import { money, coladoTotal, fileUrl } from "@/lib/api";

const AssignPopover = ({ colado, coladores, onAssign }) => {
  const [colador, setColador] = useState("");
  const [day, setDay] = useState("");
  const DAYS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
  const LABEL = { lunes: "Lunes", martes: "Martes", miercoles: "Miércoles", jueves: "Jueves", viernes: "Viernes", sabado: "Sábado", domingo: "Domingo" };
  return (
    <PopoverContent className="w-64 bg-white" align="end">
      <p className="text-sm font-medium text-slate-800 mb-2">Asignar a colador</p>
      <div className="space-y-2">
        <Select value={colador} onValueChange={setColador}>
          <SelectTrigger data-testid="assign-colador-select"><SelectValue placeholder="Elegir colador" /></SelectTrigger>
          <SelectContent>{coladores.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={day} onValueChange={setDay}>
          <SelectTrigger data-testid="assign-day-select"><SelectValue placeholder="Elegir día" /></SelectTrigger>
          <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{LABEL[d]}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="w-full bg-orange-600 hover:bg-orange-700" disabled={!colador || !day}
          onClick={() => onAssign(colado, colador, day)} data-testid="assign-confirm-btn">
          Asignar <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </PopoverContent>
  );
};

export default function BacklogPanel({ backlog, coladores, onAdd, onAssign, onEdit, onDelete }) {
  return (
    <aside className="bg-white rounded-lg border border-slate-200 flex flex-col h-full" data-testid="backlog-panel">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-slate-700" />
          <h2 className="font-display font-bold text-slate-900">Agenda semanal</h2>
          <span className="text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{backlog.length}</span>
        </div>
      </div>
      <div className="p-3">
        <Button onClick={onAdd} variant="outline" className="w-full border-dashed border-slate-300 hover:border-orange-500 hover:text-orange-600" data-testid="add-backlog-colado-btn">
          <Plus className="h-4 w-4 mr-1" /> Agregar colado pendiente
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scroll px-3 pb-3 space-y-2">
        {backlog.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-8 px-2">No hay colados pendientes por asignar.</p>
        )}
        {backlog.map((c) => (
          <div key={c.id} className="rounded-md border border-slate-200 p-3 hover:shadow-sm transition-shadow bg-slate-50/50" data-testid={`backlog-item-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-slate-800 text-sm leading-snug">{c.obra || "Colado sin nombre"}</p>
              <span className="font-display font-bold text-slate-900 text-sm whitespace-nowrap">{money(coladoTotal(c))}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{c.m2 || 0} m² × {money(c.price_per_m2)}{c.extra_amount ? ` + extra ${money(c.extra_amount)}` : ""}</p>
            <div className="flex items-center gap-1 mt-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm" className="flex-1 h-8 bg-slate-900 hover:bg-slate-800 text-xs" data-testid={`assign-btn-${c.id}`}>Asignar</Button>
                </PopoverTrigger>
                <AssignPopover colado={c} coladores={coladores} onAssign={onAssign} />
              </Popover>
              {c.pdf_file_path && (
                <a href={fileUrl(c.pdf_file_path)} target="_blank" rel="noreferrer"
                  className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-orange-600 hover:bg-orange-50" data-testid={`backlog-pdf-${c.id}`}>
                  <FileText className="h-4 w-4" />
                </a>
              )}
              <button onClick={() => onEdit(c)} className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-100" data-testid={`backlog-edit-${c.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => onDelete(c)} className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-500" data-testid={`backlog-delete-${c.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
